<template>
	<div
		ref="overlay"
		class="game-information-overlay"
	>
		<div class="content">
			<component
				:is="scoreboardComponent"
				v-if="showScoreboardScore"
				v-show="!hasPeriods || isCurrentPeriodActive"
				v-bind="scoreboardProps"
			/>
			<GameClock
				v-if="hasClock"
				v-show="!hasPeriods || isCurrentPeriodActive"
				:minutes="gameTime.minutes"
				:seconds="gameTime.seconds"
			/>
			<div
				v-if="hasCustomText"
				class="custom-text"
			>
				<div class="text">
					{{ playerCustomText }}
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import Scoreboard, { TennisScoreboard, IceHockeyScoreboard, FutsalScoreboard } from '../Scoreboard';
import GameClock from '../GameClock';
import { getScoreboardOutput, getScoreZones, getGameTime, getPeriods, getPeriod } from '@js/logic/game';
import {
	getGameTime as iceHockeyGameTime,
	getPeriods as iceHockeyPeriods,
	getPeriod as iceHockeyPeriod,
	PERIOD_TYPE,
} from '@js/logic/game/ice_hockey';
import {
	getGameTime as futsalGameTime,
	getPeriods as futsalPeriods,
	getPeriod as futsalPeriod,
} from '@js/logic/game/futsal';
import { GAME_SPORT } from '@shared/constants/game';

export default {
	name:       'GameInformation',
	components: { Scoreboard, GameClock },
	props:      {
		position: {
			type:     Number,
			required: true,
		},
		event: {
			type:     Object,
			required: true,
		},
		tags: {
			type:     Array,
			required: true,
		},
		showScoreboardScore: {
			type: Boolean,
		},
		showScoreboardTime: {
			type: Boolean,
		},
		playerCustomTextEnabled: {
			type:    Boolean,
			default: false,
		},
		playerCustomText: {
			type:    String,
			default: '',
		},
	},
	computed: {
		isTennis() {
			return this.event.sport.name === GAME_SPORT.TENNIS;
		},
		isIceHockey() {
			return this.event.sport.name === GAME_SPORT.ICE_HOCKEY;
		},
		isStreetHockey() {
			return this.event.sport.name === GAME_SPORT.STREET_HOCKEY;
		},
		isFutsal() {
			return this.event.sport.name === GAME_SPORT.FUTSAL;
		},
		isSoccer() {
			return this.event.sport.name === GAME_SPORT.SOCCER;
		},
		isFloorball() {
			return this.event.sport.name === GAME_SPORT.FLOORBALL;
		},
		hasClock() {
			return this.showScoreboardTime && !this.isTennis && !this.isIceHockey && !this.isStreetHockey && !this.isFloorball && !this.isFutsal;
		},
		hasPeriods() {
			return !this.isTennis;
		},
		scoreboardComponent() {
			if (this.isTennis) return TennisScoreboard;
			else if (this.isIceHockey || this.isFloorball || this.isStreetHockey) return IceHockeyScoreboard;
			else if (this.isFutsal) return FutsalScoreboard;
			return Scoreboard;
		},
		scoreboardProps() {
			if (this.isTennis) {
				return {
					players: this.event.participants.map((participant) => {
						return {
							id:   participant.participatable.id,
							name: participant.participatable.name,
						};
					}),
					sets: this.currentScoreZone?.sets ?? [],
				};
			} else if (this.isIceHockey || this.isFloorball || this.isStreetHockey) {
				return {
					players: this.event.participants.map((participant) => {
						return {
							id:    participant.participatable.id,
							code:  participant.participatable.club.code,
							image: participant.participatable.imageUrl,
						};
					}),
					event:            this.event,
					tags:             this.tags,
					position:         this.position,
					scoreZones:       this.scoreZones,
					periods:          this.periods,
					currentPeriod:    this.currentPeriod,
					currentScoreZone: this.currentScoreZone,
					gameTime:         this.gameTime,
					showTime:         this.showScoreboardTime,
				};
			} else if (this.isFutsal) {
				return {
					players: this.event.participants.map((participant) => {
						return {
							id:    participant.participatable.id,
							code:  participant.participatable.club.code,
							image: participant.participatable.imageUrl,
						};
					}),
					event:            this.event,
					tags:             this.tags,
					position:         this.position,
					scoreZones:       this.scoreZones,
					periods:          this.periods,
					currentScoreZone: this.currentScoreZone,
					gameTime:         this.gameTime,
					showTime:         this.showScoreboardTime,
				};
			}

			return {
				cells: getScoreboardOutput({
					event:      this.event,
					tags:       this.tags,
					timestamp:  this.position,
					scoreZones: this.scoreZones,
				}),
			};
		},
		scoreZones() {
			return getScoreZones({ event: this.event, tags: this.tags });
		},
		currentScoreZone() {
			const [currentZone] = Array.from(this.scoreZones)
				.sort((a, b) => a.start - b.start)
				.filter((zone) => zone.start <= this.position)
				.reverse();
			return currentZone;
		},
		gameTime() {
			let minutes, seconds;
			if (this.isIceHockey || this.isFloorball || this.isStreetHockey) {
				({ minutes, seconds } = iceHockeyGameTime({
					position: this.position,
					event:    this.event,
					tags:     this.tags,
					periods:  this.periods,
				}));
			} else if (this.isFutsal) {
				({ minutes, seconds } = futsalGameTime({
					position: this.position,
					event:    this.event,
					tags:     this.tags,
					periods:  this.periods,
				}));
			} else {
				if (!this.hasClock) return null;
				({ minutes, seconds } = getGameTime({
					position: this.position,
					event:    this.event,
					tags:     this.tags,
				}));
			}
			return {
				minutes: Math.floor(minutes),
				seconds: Math.floor(seconds),
			};
		},
		periods() {
			if (!this.hasPeriods) return [];
			if (this.isIceHockey || this.isFloorball || this.isStreetHockey) return iceHockeyPeriods({ event: this.event, tags: this.tags });
			if (this.isFutsal) return futsalPeriods({ event: this.event, tags: this.tags });
			return getPeriods({ event: this.event, tags: this.tags });
		},
		currentPeriod() {
			if (!this.hasPeriods) return null;
			if (this.isIceHockey || this.isFloorball || this.isStreetHockey) {
				return iceHockeyPeriod({
					event:    this.event,
					position: this.position,
					periods:  this.periods,
					tags:     this.tags,
				});
			}
			if (this.isFutsal) {
				return futsalPeriod({
					event:    this.event,
					position: this.position,
					periods:  this.periods,
					tags:     this.tags,
				});
			}
			return getPeriod({ position: this.position, periods: this.periods });
		},
		isCurrentPeriodActive() {
			if (!this.hasPeriods) return false;
			if (this.isIceHockey || this.isStreetHockey) {
				return this.currentPeriod &&
					[PERIOD_TYPE.ACTION, PERIOD_TYPE.OVERTIME_ACTION, PERIOD_TYPE.PENALTY_SHOOTOUT_ACTION]
						.includes(this.currentPeriod.type);
			}
			if (this.isFutsal) {
				return this.currentPeriod &&
					[PERIOD_TYPE.ACTION, PERIOD_TYPE.OVERTIME_ACTION, PERIOD_TYPE.PENALTY_SHOOTOUT_ACTION]
						.includes(this.currentPeriod.type);
			}
			if (this.isSoccer) {
				return [PERIOD_TYPE.ACTION, PERIOD_TYPE.OVERTIME_ACTION, PERIOD_TYPE.PENALTY_SHOOTOUT_ACTION].includes(this.currentPeriod?.type);
			}
			return this.currentPeriod?.type === PERIOD_TYPE.ACTION;
		},
		hasCustomText() {
			return this.playerCustomTextEnabled && this.playerCustomText.length > 0;
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@shared/sass/shared-variables';

.game-information-overlay {
	position: relative;
	pointer-events: none;

	.content {
		display: flex;
		position: absolute;
		transform-origin: top left;
	}
}

.custom-text {
	margin-left: 24px;

	.text {
		padding: 10px 16px;
		font-weight: 500;
		font-size: 28px;
		background: var(--color-soft-white);
		color: var(--color-shadow-grey);
		white-space: nowrap;
	}
}
</style>
